import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import type { CardOption } from './CardsList'

interface UseCardSelectionProps {
  options: Array<CardOption>
  selection?: Array<string>
  disabled: boolean
  select: 'single' | 'multiple'
  onSelectionChange: (selectedTitles: Array<string>) => void
}

interface UseCardSelectionReturn {
  selectedIndices: Set<number>
  optionClickHandlers: Array<() => void>
}

export function useCardSelection({
  options,
  selection,
  disabled,
  select,
  onSelectionChange,
}: UseCardSelectionProps): UseCardSelectionReturn {
  // Use indices for internal selection tracking to handle duplicate titles
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => {
    // Initialize from selection prop by finding matching indices
    const initialIndices = new Set<number>()
    if (selection) {
      selection.forEach(title => {
        const index = options.findIndex(opt => opt.title === title)
        if (index !== -1) {
          initialIndices.add(index)
        }
      })
    }
    return initialIndices
  })

  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange

  const isInternalUpdateRef = useRef(false)
  const prevSelectionRef = useRef(selection)

  const validTitles = useMemo(
    () => new Set(options.map(opt => opt.title)),
    [options],
  )

  useEffect(() => {
    if (
      selection !== undefined &&
      !disabled &&
      !isInternalUpdateRef.current &&
      prevSelectionRef.current !== selection
    ) {
      // Convert titles to indices for internal tracking
      const newIndices = new Set<number>()
      const usedIndices = new Set<number>()
      selection.forEach(title => {
        // Find the first unused index with this title to handle duplicates properly
        const index = options.findIndex((opt, idx) => opt.title === title && !usedIndices.has(idx))
        if (index !== -1) {
          newIndices.add(index)
          usedIndices.add(index)
        }
      })

      const currentArray = Array.from(selectedIndices)
      const hasChanges =
        newIndices.size !== currentArray.length ||
        currentArray.some(idx => !newIndices.has(idx))

      if (hasChanges) {
        setSelectedIndices(newIndices)
      }
    }
    prevSelectionRef.current = selection
    isInternalUpdateRef.current = false
  }, [selection, disabled, selectedIndices, options])

  const handleOptionClick = useCallback((index: number) => {
    if (disabled) {
      return
    }

    isInternalUpdateRef.current = true

    setSelectedIndices(prevSelected => {
      const newSelection = new Set(prevSelected)

      if (select === 'single') {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.clear()
          newSelection.add(index)
        }
      } else {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.add(index)
        }
      }

      // Convert indices back to titles for the callback
      const selectedTitles = Array.from(newSelection).map(idx => options[idx]?.title).filter(Boolean)

      const otherSelections = (selection ?? []).filter(s => !validTitles.has(s))
      const mergedSelection = [...otherSelections, ...selectedTitles]

      onSelectionChangeRef.current(mergedSelection)
      return newSelection
    })
  }, [disabled, select, selection, validTitles, options])

  const optionClickHandlers = useMemo(() =>
    options.map((_, index) => () => handleOptionClick(index)),
  [options, handleOptionClick])

  return { selectedIndices, optionClickHandlers }
}
